import React, { Fragment } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { dashboardAsset } from "./dashboardAssets";

marked.use({
  gfm: true,
  breaks: false,
});

const FALLBACK_CHRONICLE_IMAGE = dashboardAsset("07_ai_chronicle_lore_terminal.png");

/** @param {Record<string, unknown> | null | undefined} post */
export function getChronicleImageUrl(post) {
  if (!post || typeof post !== "object") return FALLBACK_CHRONICLE_IMAGE;
  const order = ["heroImage", "featuredImage", "thumbnailImage", "socialShareImage"];
  for (const k of order) {
    const u = post[k];
    if (typeof u === "string" && u.trim()) return u.trim();
  }
  return FALLBACK_CHRONICLE_IMAGE;
}

export function formatChronicleDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function stripLeadingCodeFence(s) {
  let t = String(s ?? "")
    .trim()
    .replace(/^\uFEFF/, "");
  while (t.startsWith("```")) {
    const m = /^```(?:markdown|md|json)?\s*\r?\n?([\s\S]*?)\r?\n```\s*/i.exec(t);
    if (!m) break;
    const inner = m[1].trim();
    if (!inner || inner === t) break;
    t = inner;
  }
  return t;
}

function normalizeRawArticleString(s) {
  return stripLeadingCodeFence(String(s ?? "").trim());
}

function stripLeakedAiEditorNotesFromMarkdown(md) {
  let s = String(md ?? "");
  s = s.replace(
    /^>\s*(?:\*\*)?Image direction[^\n]*(?:\*\*)?\s*\n(?:>[^\n]*\n?)*/gim,
    ""
  );
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

function finalizeReaderMarkdown(md) {
  return stripLeakedAiEditorNotesFromMarkdown(String(md ?? "")).trim();
}

function extractMarkdownFromAiResponseObject(o) {
  if (!o || typeof o !== "object" || Array.isArray(o)) return "";
  if (o.type === "full_package" && o.draft && typeof o.draft.content === "string" && o.draft.content.trim()) {
    return o.draft.content.trim();
  }
  if (o.type === "full_package" && typeof o.article === "string" && o.article.trim()) {
    return o.article.trim();
  }
  if (typeof o.content === "string" && o.content.trim()) {
    if (o.type === "draft" || o.suggestedTitle != null || o.excerpt != null) {
      return o.content.trim();
    }
  }
  if (o.type === "outline" && typeof o.outline === "string" && o.outline.trim()) {
    return o.outline.trim();
  }
  if (typeof o.markdown === "string" && o.markdown.trim()) {
    return o.markdown.trim();
  }
  if (typeof o.body === "string" && o.body.trim()) {
    return o.body.trim();
  }
  if (typeof o.article === "string" && o.article.trim()) {
    return o.article.trim();
  }
  return "";
}

function coerceArticleBodyMarkdown(raw) {
  let s = normalizeRawArticleString(raw);
  if (!s) return s;
  let guard = 0;
  while (s.startsWith("{") && guard++ < 6) {
    try {
      const o = JSON.parse(s);
      if (Array.isArray(o)) break;
      const next = extractMarkdownFromAiResponseObject(o);
      if (!next || !String(next).trim()) break;
      const normalized = normalizeRawArticleString(next);
      if (!normalized || normalized === s) break;
      s = normalized;
    } catch {
      break;
    }
  }
  return s;
}

/**
 * @returns {{ kind: "empty" | "bad-json" | "blocks" | "html" | "markdown" | "plain"; blocks?: unknown[]; html?: string; md?: string; plain?: string }}
 */
export function resolveArticleBodySource(post) {
  if (!post) return { kind: "empty" };
  if (Array.isArray(post.contentBlocks) && post.contentBlocks.length) {
    return { kind: "blocks", blocks: post.contentBlocks };
  }
  const fmt = String(post.contentFormat || "markdown").toLowerCase();
  const c = post.content;
  if (Array.isArray(c) && c.length) {
    return { kind: "blocks", blocks: c };
  }
  if (typeof c !== "string" || !String(c).trim()) {
    return { kind: "empty" };
  }
  const text = normalizeRawArticleString(c);

  if (fmt === "json") {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed) && parsed.length) {
        return { kind: "blocks", blocks: parsed };
      }
      const extracted = extractMarkdownFromAiResponseObject(parsed);
      if (extracted) {
        return {
          kind: "markdown",
          md: finalizeReaderMarkdown(extracted),
        };
      }
    } catch {
      const md = coerceArticleBodyMarkdown(text);
      if (md !== text && String(md).trim()) {
        return {
          kind: "markdown",
          md: finalizeReaderMarkdown(md),
        };
      }
    }
    return { kind: "bad-json" };
  }

  if (fmt === "html") {
    const trimmed = text.trimStart();
    if (!/^<[a-z!]/i.test(trimmed)) {
      const md = coerceArticleBodyMarkdown(text);
      if (String(md).trim()) {
        const cleaned = finalizeReaderMarkdown(md);
        return { kind: "markdown", md: cleaned };
      }
    }
    return { kind: "html", html: text };
  }

  const md = coerceArticleBodyMarkdown(text);
  if (!String(md).trim()) {
    return { kind: "empty" };
  }
  const cleaned = finalizeReaderMarkdown(md);
  if (!String(cleaned).trim()) {
    return { kind: "empty" };
  }
  return { kind: "markdown", md: cleaned };
}

function sanitizeArticleHtml(html) {
  return DOMPurify.sanitize(String(html ?? ""), {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["id", "class"],
  });
}

export function markdownToSafeHtml(md) {
  const src = finalizeReaderMarkdown(md);
  if (!src) return "";
  try {
    const raw = marked.parse(src, { async: false });
    return sanitizeArticleHtml(String(raw));
  } catch {
    return `<p class="chr-body__p chr-body__muted">This record could not be rendered.</p>`;
  }
}

function safeExternalHref(href) {
  const s = String(href || "").trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/") && !s.startsWith("//")) return s;
  return undefined;
}

function renderJsonBlocks(blocks) {
  if (!Array.isArray(blocks)) return null;
  return blocks.map((block, i) => {
    if (!block || typeof block !== "object") return null;
    const key = `${block.type || "unknown"}-${i}`;
    switch (block.type) {
      case "heading": {
        const level = Number(block.level) || 2;
        const Tag = level >= 3 ? "h3" : "h2";
        return (
          <Tag key={key} id={block.id ? String(block.id) : undefined} className="chr-body__h">
            {block.text}
          </Tag>
        );
      }
      case "paragraph":
        return (
          <p key={key} className="chr-body__p">
            {block.text}
          </p>
        );
      case "pullquote":
        return (
          <blockquote key={key} className="chr-body__pullquote">
            {block.text}
          </blockquote>
        );
      case "takeaways":
        return (
          <div key={key} className="chr-body__takeaways">
            <p className="chr-body__label">Key takeaways</p>
            <ul>
              {(block.items || []).map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          </div>
        );
      case "actionbox":
        return (
          <div key={key} className="chr-body__actionbox">
            <span className="chr-body__label">Action step</span>
            {block.text ? <p className="chr-body__p">{block.text}</p> : null}
            {(() => {
              const href = safeExternalHref(block.href);
              const label = block.buttonLabel || block.ctaLabel;
              if (!href || !label) return null;
              return (
                <a className="chr-body__linkbtn" href={href} target="_blank" rel="noopener noreferrer">
                  {String(label)}
                </a>
              );
            })()}
          </div>
        );
      case "image": {
        const src = typeof block.src === "string" ? block.src.trim() : "";
        if (!src || !/^https?:\/\//i.test(src) || /^javascript:/i.test(src)) return null;
        return (
          <figure key={key} className="chr-body__figure">
            <img src={src} alt={block.alt ? String(block.alt) : ""} loading="lazy" decoding="async" />
            {block.caption ? <figcaption>{block.caption}</figcaption> : null}
          </figure>
        );
      }
      case "reflection":
        return (
          <aside key={key} className="chr-body__aside" aria-label="Reflection">
            <p className="chr-body__label">Reflection</p>
            <p className="chr-body__p">{block.text}</p>
          </aside>
        );
      case "cta": {
        const href = safeExternalHref(block.href);
        return (
          <div key={key} className="chr-body__actionbox chr-body__cta">
            <span className="chr-body__label">{block.label || "Transmission"}</span>
            {block.text ? <p className="chr-body__p">{block.text}</p> : null}
            {href ? (
              <a className="chr-body__linkbtn" href={href} target="_blank" rel="noopener noreferrer">
                {String(block.buttonLabel || "Learn more")}
              </a>
            ) : null}
          </div>
        );
      }
      default:
        return null;
    }
  });
}

function plainFallback(text) {
  const t = String(text ?? "").trim();
  if (!t) {
    return (
      <p className="chr-body__p chr-body__muted">No archive body for this record.</p>
    );
  }
  const paras = t.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return paras.map((p, i) => (
    <p key={i} className="chr-body__p">
      {p.split("\n").map((line, j) => (
        <Fragment key={j}>
          {j > 0 ? <br /> : null}
          {line}
        </Fragment>
      ))}
    </p>
  ));
}

/**
 * Safe article body: markdown → marked → DOMPurify; html → DOMPurify; json → React blocks; plain fallback.
 * @param {{ post: Record<string, unknown> | null | undefined }} props
 */
export default function ChronicleArticleBody({ post }) {
  const src = resolveArticleBodySource(post);

  if (src.kind === "empty") {
    return (
      <article className="chr-body">
        {plainFallback(typeof post?.content === "string" ? post.content : "")}
      </article>
    );
  }

  if (src.kind === "bad-json") {
    return (
      <article className="chr-body">
        <p className="chr-body__p chr-body__muted">
          This record is stored in an archive format that could not be parsed. If you manage the vault, re-save the
          entry as Markdown or valid JSON blocks.
        </p>
      </article>
    );
  }

  if (src.kind === "blocks") {
    return <article className="chr-body chr-body--blocks">{renderJsonBlocks(src.blocks)}</article>;
  }

  if (src.kind === "html") {
    const safe = sanitizeArticleHtml(src.html);
    return (
      <article
        className="chr-body chr-body--html"
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }

  const mdHtml = markdownToSafeHtml(src.md || "");
  return (
    <article
      className="chr-body chr-body--md"
      dangerouslySetInnerHTML={{ __html: mdHtml }}
    />
  );
}
